/**
 * Genuine content-review metadata for a clinician profile.
 * Only populate from a real editorial/clinical review — never from the
 * current date, file mtime, commit date or deployment date.
 */
export interface ClinicianReviewMetadata {
  /** ISO date (YYYY-MM-DD) of the actual content review */
  reviewedAt: string
  reviewedBy: string
}

/** Shared data model for every doctor / clinician object */
export interface TeamMember {
  id: number
  name: string
  title: string
  role: TeamMemberRole

  /**
   * Approved short display name for CTAs (e.g. "Dr. Meena").
   * When absent, surfaces fall back to the first name — no honorific is
   * ever guessed.
   */
  displayName?: string

  /**
   * Neutral bucket for professional credentials. Current source data mixes
   * degrees, statutory registrations and association/membership IDs, and
   * cannot be reliably separated without owner confirmation (documented
   * data gap) — do not reclassify entries based on identifier format.
   */
  credentials?: string[]

  /**
   * Statutory professional registrations, once confirmed by the owner as
   * distinct from `credentials`. Currently unpopulated.
   */
  registrations?: string[]

  imageUrl: string

  /** Cut-out/isolated image of the doctor for modal display (optional) */
  cutOutUrl?: string

  specialty: string
  yearsExperience: number

  /** Languages the clinician can comfortably work in */
  languagesKnown: string[] // e.g. ['English', 'Hindi', 'Kannada']

  /** Separate expertise by population */
  expertise: Expertise

  education?: string[]
  bio: string
  philosophy?: string
  publications?: string[]

  /** Present only when a genuine content review has taken place */
  review?: ClinicianReviewMetadata
}

/** Structured expertise by population type */
export interface Expertise {
  children: string[] // paediatric & adolescent focus
  adults: string[] // adult & geriatric focus
}

/** Legacy format where expertise was a flat array */
export type LegacyExpertise = string[]

/** Union type for backwards compatibility during migration */
export type ExpertiseField = Expertise | LegacyExpertise

/** Type guard to check if expertise is in legacy format */
export function isLegacyExpertise(
  expertise: ExpertiseField,
): expertise is LegacyExpertise {
  return Array.isArray(expertise)
}

/** Type guard to check if expertise is in modern format */
export function isModernExpertise(
  expertise: ExpertiseField,
): expertise is Expertise {
  return (
    !Array.isArray(expertise) &&
    typeof expertise === 'object' &&
    'children' in expertise &&
    'adults' in expertise
  )
}

/**
 * Normalize expertise to consistent structure
 * Handles both legacy (string[]) and modern ({ children, adults }) formats
 */
export function normalizeExpertise(expertise: ExpertiseField): Expertise {
  // Already in correct format
  if (isModernExpertise(expertise)) {
    return expertise
  }

  // Legacy format - assume all items are adult expertise
  if (isLegacyExpertise(expertise)) {
    return {
      children: [],
      adults: expertise,
    }
  }

  // Fallback for unexpected formats
  console.warn('Unexpected expertise format:', expertise)
  return {
    children: [],
    adults: [],
  }
}

/**
 * Helper to check if a team member has any expertise listed
 */
export function hasExpertise(member: TeamMember): boolean {
  const normalized = normalizeExpertise(member.expertise)
  return normalized.children.length > 0 || normalized.adults.length > 0
}

/**
 * Helper to get total expertise count
 */
export function getExpertiseCount(member: TeamMember): number {
  const normalized = normalizeExpertise(member.expertise)
  return normalized.children.length + normalized.adults.length
}

/**
 * Approved short name for CTAs. Uses the explicit displayName when set;
 * otherwise falls back to the first name without guessing any honorific.
 */
export function getDisplayName(member: TeamMember): string {
  return member.displayName ?? member.name.split(' ')[0]
}

/**
 * Helper to get the appropriate image URL for display
 * Prefers cutOutUrl for modals, falls back to imageUrl
 */
export function getDisplayImage(
  member: TeamMember,
  preferCutOut: boolean = false,
): string {
  if (preferCutOut && member.cutOutUrl) {
    return member.cutOutUrl
  }
  return member.imageUrl
}

// ────────────────────────────────────────────────────────────
// Discriminated Union Types for Team Members
// ────────────────────────────────────────────────────────────

/** All possible team member roles */
export type TeamMemberRole =
  | 'Psychiatrist'
  | 'Psychologist'
  | 'Therapist'
  | 'Counselor'

/** Base interface for all team members */
interface BaseTeamMember {
  id: number
  name: string
  title: string
  displayName?: string
  credentials?: string[]
  registrations?: string[]
  imageUrl: string
  cutOutUrl?: string
  specialty: string
  yearsExperience: number
  languagesKnown: string[]
  expertise: Expertise
  education?: string[]
  bio: string
  philosophy?: string
  publications?: string[]
  review?: ClinicianReviewMetadata
}

/** Psychologist-specific team member */
export interface PsychologistMember extends BaseTeamMember {
  role: 'Psychologist'
  // Psychologists might have specific fields in the future
}

/** Psychiatrist-specific team member */
export interface PsychiatristMember extends BaseTeamMember {
  role: 'Psychiatrist'
  // Psychiatrists might have specific fields in the future
}

/** Therapist-specific team member */
export interface TherapistMember extends BaseTeamMember {
  role: 'Therapist'
  // Therapists might have specific fields in the future
}

/** Counselor-specific team member */
export interface CounselorMember extends BaseTeamMember {
  role: 'Counselor'
  // Counselors might have specific fields in the future
}

/** Discriminated union of all team member types */
export type TypedTeamMember =
  | PsychologistMember
  | PsychiatristMember
  | TherapistMember
  | CounselorMember

// ────────────────────────────────────────────────────────────
// Type Guards for Runtime Type Checking
// ────────────────────────────────────────────────────────────

/** Type guard to check if member is a psychologist */
export function isPsychologist(
  member: TeamMember,
): member is PsychologistMember {
  return member.role === 'Psychologist'
}

/** Type guard to check if member is a psychiatrist */
export function isPsychiatrist(
  member: TeamMember,
): member is PsychiatristMember {
  return member.role === 'Psychiatrist'
}

/** Type guard to check if member is a therapist */
export function isTherapist(member: TeamMember): member is TherapistMember {
  return member.role === 'Therapist'
}

/** Type guard to check if member is a counselor */
export function isCounselor(member: TeamMember): member is CounselorMember {
  return member.role === 'Counselor'
}

/** Helper to check if member should use PsychologistCard component */
export function usesPsychologistCard(member: TeamMember): boolean {
  return isPsychologist(member) || isPsychiatrist(member)
}

/** Helper to format member role for display */
export function formatRole(role: TeamMemberRole): string {
  const roleMap: Record<TeamMemberRole, string> = {
    Psychiatrist: 'Psychiatrist',
    Psychologist: 'Clinical Psychologist',
    Therapist: 'Therapist',
    Counselor: 'Counselor',
  }
  return roleMap[role] || role
}
